import { type Action } from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema';
import { type AnalyticsEventPayload, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { useRovoAgentActionAnalytics } from '@atlaskit/rovo-agent-analytics/actions';
import type {
	ReasoningMode,
	RovoApp,
	SourceMode,
} from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import type { BrowserContextSanitizer } from '@atlassian/conversation-assistant-chat-prompt-input/types';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import type {
	FollowUpSource,
	InteractionSource,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import type { ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import type { TrackAIEventPayloads } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import type {
	ApiTaskCurrentStep,
	AssistanceService,
	AssistantMessage,
	ConversationChannelTask,
	StreamActionComplete,
	StreamActionClassification,
	StreamActionPart,
	StreamActionPrepare,
	StreamMessage,
	SpaceSourceRecommendation,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	MessageActionPayload,
	MessageActionPayloadContext,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	type ContentType,
	type useChatContextProvider,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	type ChatStatus,
	type ConversationError,
	type StoreError,
} from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	type ChatStorePrivateState,
	type ExecutionSegment,
	type StoreChatItem,
	type TaskProgressState,
	type TodoSnapshot,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { type StagingAreaEntryPoint } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import type { UnifiedTask } from '@atlassian/conversation-assistant-store/controllers/unified-tasks';

import type { RovoChatCapability } from '../../common/hooks/useRovoChatCapabilities';
import { type StoreErrorFlagId } from '../../common/ui/hooks/use-show-store-error-flag/StoreErrorFlagId';
import type { PromptSentContext } from '../../types';
import type { ViewDeadAirBroker } from '../../ui/view-dead-air-tracker/types';
import type { StreamLifecycleState } from '../bridge-api/streaming-lifecycle/StreamLifecycleState';

export const TAGS = {
	INSIGHTS: 'insights',
};

/**
 * Subset of unified tasks store actions injected into the chat-store container.
 * Mirrors the action signatures the chat-store needs to call without taking a
 * full dependency on the unified-tasks module from inside its action bodies.
 *
 * Wired in `main.tsx` as the task source of truth for chat-store actions.
 */
export type UnifiedTasksContainerDep = {
	createTaskFromStream: (params: {
		conversationId: string;
		taskId: string;
		title: string;
		updatedAt?: string;
		messageId?: string;
	}) => void;
	setTaskProgressState: (params: {
		taskId?: string;
		conversationId?: string;
		progressState: TaskProgressState;
		title?: string;
		seekingCompletedAck?: boolean;
		messageId?: string;
		completionMessageId?: string;
		updatedAt?: string;
		currentMessageId?: string | null;
		createdAt?: string;
		startedAt?: string | null;
		completedAt?: string | null;
		executionSegments?: ExecutionSegment[] | null;
		// `null` clears the field; `undefined` leaves it unchanged.
		currentStep?: ApiTaskCurrentStep | null;
		resultSummary?: string | null;
		errorMessage?: string | null;
	}) => void;
	setTaskTodoSnapshot: (params: {
		taskId?: string;
		conversationId?: string;
		todoSnapshot: TodoSnapshot;
	}) => void;
	ackTaskCompleted: (params: { taskId: string }) => void;
	mergeConversationChannelTasks: (remoteTasks: ConversationChannelTask[]) => void;
	/** Imperative read-only accessor — same shape the unified store exposes. */
	getTaskById: (taskId: string) => UnifiedTask | undefined;
	/**
	 * Imperative read-only accessor — returns the active task for a conversation.
	 * Prefers the most recent non-terminal task; falls back to most recent overall.
	 */
	getActiveTaskForConversation: (conversationId: string) => UnifiedTask | undefined;
};

export type ChatStoreState = ChatStorePrivateState & {
	checkActiveConversationMatchesSource: (id?: string) => boolean;
};

/** Default is void; use ChatStoreAction<Promise<string | undefined>> for actions that return a value. */
export type ChatStoreAction<T = void | Promise<void>> = Action<
	ChatStorePrivateState,
	ChatStoreContainerProps,
	T
>;

export type ChatStoreContainerProps = {
	onPromptSent?: (context: PromptSentContext) => void | Promise<void>;
	isGlobal?: boolean;
	siteId: string;
	capabilities?: RovoChatCapability[];
	assistanceService: AssistanceService;
	experienceTracker: ExperienceTrackerAPI;
	sendAnalyticsEvent: (
		payload: AnalyticsEventPayload | UIAnalyticsEvent,
		options?: { removeDefaultTags?: boolean },
	) => void;
	trackAIMAUEvent: (event: TrackAIEventPayloads) => void;
	chatContextStoreActions: ReturnType<typeof useChatContextProvider>;
	setChatStatus: (status: ChatStatus) => void;
	setConversationError: (conversationError: ConversationError | undefined) => void;
	getIsChatMounted: () => boolean;
	startReconnection: (conversationId: string, messageId: string) => boolean;
	endReconnection: (conversationId: string) => void;
	isReconnecting: (conversationId: string) => boolean;
	getMessages: (conversationId: string) => StoreChatItem[];
	setMessages: (conversationId: string, messages: StoreChatItem[]) => void;
	setRovoEnabled: (isEnabled: boolean, reason?: string) => void;
	processStreamResearch: (params: {
		messageId: string;
		streamMessage: StreamMessage;
		isDeepResearch: boolean;
	}) => void;
	markIncompleteResearchesErrored: (params: { messageId: string }) => void;
	completeAllSteps: (params: { messageId: string }) => void;
	updateResearchMessageId: (params: { messageId: string; newMessageId: string }) => void;
	clearMessageResearch: (params: { messageId: string }) => void;
	addImplicitResearchPlan: (params: {
		messageId: string;
		content: string;
		roundId: number;
	}) => void;
	resetHasReceivedAnswerPart: (params: { messageId: string }) => void;
	initChatInitiator: (initiator?: string) => void;
	getChatInitiator: () => string;
	insertHighlightActionsEditorPlaceholder: (
		placeholder: DocNode,
		options?: { skipPlaceholderPrompt?: boolean },
	) => void;
	processMessageStreamAction?: (
		invocationId: string,
		message: StreamActionPart | StreamActionPrepare,
		traceId: string | undefined,
		requestId?: string,
		prompt?: DocNode,
		attributes?: {
			singleInstrumentationId?: string | undefined;
			experience?: CreationContextParams['experience'];
		},
		agent?: { id: string; name: string; identityAccountId: string | null },
	) => void;
	processMessageStreamComplete?: (
		invocationId: string,
		streamAction: StreamActionComplete,
		traceId: string | undefined,
		requestId?: string,
		attributes?: {
			singleInstrumentationId?: string;
			experience?: CreationContextParams['experience'];
		},
	) => void;
	processMessageStreamError?: (errorInfo: {
		actionKey?: string;
		errorType?: string;
		errorMessage?: string;
		traceId?: string;
	}) => void;
	processMessageStreamActionClassification?: (
		invocationId: string,
		streamActionClassification: StreamActionClassification,
		traceId: string | undefined,
		requestId?: string,
		attributes?: { singleInstrumentationId?: string },
	) => void;
	broadcastConfirmedActions?: (
		invocationId: string,
		actions: MessageActionPayloadContext[],
	) => void;
	broadcastStreamStatus?: (
		status: StreamLifecycleState,
		streamId: string,
		content?: { id: string | number; type: ContentType },
	) => void;
	setStagingAreaMode: (
		mode: 'inline' | 'modal' | undefined,
		entryPoint: StagingAreaEntryPoint | undefined,
	) => void;
	setMessageIdSelectedForPreview: (messageId: string) => void;
	onTempMessageIdReplaced?: (oldId: string, newId: string) => void;
	skipAutoConversationSelection?: boolean;
	onNewConversationStartingWithSendMessage: (
		newConversationId: string,
		mode: {
			deepResearchEnabled?: boolean;
			thinkDeeperEnabled?: boolean;
			alphaExtendedThinkingMode?: 'MARATHON' | 'LONG_HORIZON';
			fastModeEnabled?: boolean;
			planModeEnabled?: boolean;
			webSearchEnabled?: boolean;
			sourceMode?: SourceMode;
			useCurrentPageContext?: boolean;
			appFilters?: RovoApp[];
		},
	) => void;
	browserContextSanitizer?: BrowserContextSanitizer;
	getStoreUserMessagesPreference?: () => boolean;
	trackAgentEvent: ReturnType<typeof useRovoAgentActionAnalytics>['trackAgentEvent'];
	viewDeadAirBroker?: ViewDeadAirBroker | null;
	showFlag?: (flagId: StoreErrorFlagId) => void;
	setReasoningMode: (key: string, reasoningMode: ReasoningMode) => void;
	/**
	 * Unified tasks store actions, injected via container deps so chat-store
	 * actions can write to the tasks store without using imperative
	 * `defaultRegistry` access.
	 */
	unifiedTasksActions?: UnifiedTasksContainerDep;
	/**
	 * Generic stream-event callback invoked when a `SPACE_SOURCE_RECOMMENDATIONS`
	 * message arrives. The chat-store stays unaware of Spaces state — the host
	 * (e.g. `main.tsx`) decides where to route the recommendations.
	 */
	onSpaceSourceRecommendations?: (
		spaceId: string,
		recommendations: SpaceSourceRecommendation[],
	) => void;
};

export type MessageSource = {
	interactionSource: InteractionSource;
	followUpSource: FollowUpSource;
	invokedFrom?: string;
	/**
	 * **IMPORTANT — enumerated constants only.**
	 * This value is forwarded directly into analytics payloads and must be a
	 * controlled, hard-coded string constant (e.g. `'aiChat'`, `'rovoLabsChat'`,
	 * `'remixImageGen'`). It must **never** be derived from user input, API
	 * responses, URL parameters, or any other runtime-variable source.
	 * Passing user-generated content here would constitute a HOT-120175-style
	 * analytics data-leak and could expose PII in telemetry pipelines.
	 */
	aiFeatureName?: string;
	/**
	 * **IMPORTANT — enumerated constants only.** Same constraints as `aiFeatureName`.
	 * Surfaced as the `entryPoint` attribute on AIMAU events to mark the originating
	 * route (e.g. the Create with Rovo blank-object experience). Kept separate from
	 * `invokedFrom` so it does not clobber AIFC MAU attribution.
	 */
	entryPoint?: string;
};

export type ResearchStreamState = {
	clientRoundCounter: number;
	hasAccumulatedAnswerContent: boolean;
};

export type InactivityWatchdogConfig = {
	baseMs: number;
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void;
};

export type BulkResolveConversationActionsArgs<TActionResult = unknown> = {
	conversationId: string;
	messageId: string;
	payloads: MessageActionPayload[];
	/** generic processor function for each action type that needs to return a response such as Jira issue creation */
	actionContentProcessor?: (message: AssistantMessage) => TActionResult;
	skipSuccessOptimisticAssistantResponse?: boolean;
};

export type BulkResolveConversationUIActionsArgs<TActionResult = unknown> = {
	conversationId: string;
	messageId: string;
	payloads: MessageActionPayload[];
	/** generic processor function for each action type that needs to return a response such as Jira issue creation */
	actionContentProcessor?: (message: AssistantMessage) => TActionResult;
	skipSuccessOptimisticAssistantResponse?: boolean;
};

export type FetchConversationArgs = {
	conversationId?: string;
	messageIdSelectedForPreview?: string | null;
	/**
	 * Mount/remount callers can opt into keeping an already-hydrated idle
	 * conversation intact. Default fetch callers still refresh as before.
	 */
	skipIfConversationAlreadyLoaded?: boolean;
};

export type PrefetchConversationMessagesArgs = {
	conversationId: string;
	/**
	 * When `true`, sets `skipNextFetch: true` on the conversation after a
	 * successful prefetch, suppressing the cold-path `fetchConversation` that
	 * `<ChatHistory>` dispatches on its next mount. Callers must opt-in: the
	 * default (`false`) leaves the cold-path retry/fetch path untouched, so
	 * future callers that want a pure cache-warm without poisoning the next
	 * fetch don't get surprised.
	 */
	skipNextFetch?: boolean;
	/**
	 * Fired synchronously before the wire request kicks off, but only when this
	 * call actually issues a fetch (not when an existing in-flight or already-
	 * loaded conversation short-circuits it)
	 */
	onStart?: () => void;
	onSuccess?: () => void;
	onError?: (error: Error) => void;
};

export type SendMessageExperienceNameParamsWithTaskMode = {
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	taskModeEnabled?: boolean;
	fastModeEnabled?: boolean;
	isCustomRovoAgent?: boolean;
};

export type WarmConversationCacheArgs = {
	conversation: {
		id: string;
		name?: string;
		timeCreated?: string;
		agentId?: string;
	};
	forceRefresh?: boolean;
	onSuccess?: () => void;
	onError?: (error: Error) => void;
};

export type ErrorReason =
	| 'hipaa'
	| 'aupViolation'
	| 'channelNotFound'
	| 'noActiveConversation'
	| 'rateLimited'
	| 'generic';

export type ActionErrorContext<Args = unknown> = {
	args: Args;
	error: unknown;
	storeError: StoreError;
	containerProps: ChatStoreContainerProps;
	errorReason: ErrorReason;
};

export type ActionErrorHandlerResult = void | { proceedToDefaultHandling: true };

/**
 * Error handler defined at action definition time (in createSafeAction options).
 *
 * Return type:
 * - `void` or `undefined`: Error was handled, skip default handling
 * - `{ proceedToDefaultHandling: true }`: Delegate to default handling
 *
 * @example
 * ```ts
 * createSafeAction(name, actionFn, {
 *   actionErrorHandler: (context) => {
 *     // Custom handling for this action
 *     return { proceedToDefaultHandling: true };
 *   },
 * });
 * ```
 */
export type ActionErrorHandler<Args = unknown> = (
	context: ActionErrorContext<Args>,
) => ActionErrorHandlerResult;

/**
 * Error callback for action invocation time (passed in action args).
 * This type omits the `onError` argument from the error handler's arguments to prevent recursive invocation.
 * Use `OnErrorCallback<ActionArgsType>` when defining `onError` arg.
 *
 * @example
 * ```ts
 * // In args type definition:
 * type MyActionArgs = {
 *   someParam: string;
 *   onError?: OnErrorCallback<MyActionArgs>;
 * };
 *
 * // At invocation:
 * myAction({
 *   someParam: 'value',
 *   onError: ({ error, errorReason, args }) => {
 *     // Custom handling defined by invoker
 *     return { proceedToDefaultHandling: true };
 *   },
 * });
 * ```
 */
export type OnErrorCallback<Args> = (
	context: ActionErrorContext<Omit<Args, 'onError'>>,
) => ActionErrorHandlerResult;

export type ResolveConversationActionArgs<TActionResult = unknown> = {
	conversationId: string;
	messageId: string;
	payload: MessageActionPayload;
	/** generic processor function for each action type that needs to return a response such as Jira issue creation */
	actionContentProcessor?: (message: AssistantMessage) => TActionResult;
	skipSuccessOptimisticAssistantResponse?: boolean;
	/** Skip tracking experience when called from bulk action (to avoid noise from individual failures) */
	skipExperienceTracking?: boolean;
	onError?: OnErrorCallback<ResolveConversationActionArgs<TActionResult>>;
};

export type FetchConversationChannelsDataArgs = {
	isInitialLoad?: boolean;
	reconcileFirstPage?: boolean;
	onError?: OnErrorCallback<FetchConversationChannelsDataArgs>;
};
